import React from "react";
import {
  Box,
  Stack,
  Typography,
  Tabs,
  Tab,
  Avatar,
  styled,
  Badge
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FavoriteButton } from "pages/main/components/UserCard";
import { useTranslation } from "react-i18next";
import { getInitials } from "assets/utils";

const BACKGROUND_COLOR = "#a73ef7";

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: 'white',
    color: 'white',
    opacity: 0.8,
    boxShadow: `0 0 0 2px ${BACKGROUND_COLOR}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      border: '1px solid currentColor',
      content: '""',
    },
  },
}));

// --- SELF-CONTAINED STYLED HEADER WITH CSS VARIABLE ANIMATION ---
const HeaderBox = styled(Box)(() => ({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  zIndex: 1000,
  backgroundColor: BACKGROUND_COLOR,
  borderBottom: "1px solid rgba(255,255,255,0.2)",

  // CSS Variable driving the smooth proportional slide & fade
  "--scroll-progress": 0,
  transform: "translate3d(0, calc(-100% * (1 - var(--scroll-progress))), 0)",
  //opacity: "var(--scroll-progress)",

  visibility: "hidden",
  pointerEvents: "none",
  willChange: "transform, opacity",
  backfaceVisibility: "hidden",

  // Toggled via JS class for visibility control
  "&.active-header": {
    visibility: "visible",
    pointerEvents: "auto",
  },
}));

function PlayerAvatar({ initials, active }) {
  const avatar = <Avatar sx={{ bgcolor: (theme) => theme.palette.primary.main, fontSize: 14, width: 40, height: 40 }}>{initials}</Avatar>;
  
  return active ? <StyledBadge
    overlap="circular"
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    variant="dot"
  >
    {avatar}
  </StyledBadge> : avatar;
}

export default function PlayerNavigationHeader({
  sections,
  data,
}) {
  const STATIC_OFFSET = 130;
  const SHOW_START = 300;
  const SHOW_END = 450;

  const headerRef = React.useRef(null);
  const animationFrameRef = React.useRef(null);
  const activeSectionRef = React.useRef("overview");

  // Performance caches
  const lastProgressRef = React.useRef(-1);
  const lastDetectionTimeRef = React.useRef(0);
  const cachedToolbarHeightRef = React.useRef(56);

  const [activeSection, setActiveSection] = React.useState("overview");
  const [availableSections, setAvailableSections] = React.useState([]);

  const theme = useTheme();

  // Cache toolbar height on resize
  React.useEffect(() => {
    const updateToolbarHeight = () => {
      const el = document.getElementById("player-navigation-toolbar");
      if (el) {
        cachedToolbarHeightRef.current = el.getBoundingClientRect().height;
      }
    };
    updateToolbarHeight();
    window.addEventListener("resize", updateToolbarHeight, { passive: true });
    return () => window.removeEventListener("resize", updateToolbarHeight);
  }, []);

  React.useEffect(() => {
    const existingSections = sections.filter(({ id }) =>
      Boolean(document.getElementById(id))
    );

    setAvailableSections(existingSections);

    setActiveSection(current => {
      const currentStillExists = existingSections.some(
        section => section.id === current
      );

      const nextSection = currentStillExists
        ? current
        : existingSections[0]?.id ?? "";

      activeSectionRef.current = nextSection;

      return nextSection;
    });
  }, [sections, data]);

  const getHeaderOffset = React.useCallback(() => {
    return cachedToolbarHeightRef.current + STATIC_OFFSET;
  }, []);

  const scrollToSection = React.useCallback(
    id => {
      const element = document.getElementById(id);

      if (!element) return;

      const sectionOffset =
        sections.find(section => section.id === id)
          ?.offset ?? 0;

      const targetTop =
        element.getBoundingClientRect().top +
        window.scrollY -
        getHeaderOffset() +
        sectionOffset;

      activeSectionRef.current = id;
      setActiveSection(id);

      window.scrollTo({
        top: targetTop,
        behavior: "smooth",
      });
    },
    [sections, getHeaderOffset]
  );

  React.useEffect(() => {
    const updateFromScroll = () => {
      animationFrameRef.current = null;

      const scrollY = window.scrollY;
      const now = performance.now();
      const header = headerRef.current;

      // 1. Calculate proportional progress (0 to 1)
      const progress = Math.min(
        1,
        Math.max(
          0,
          (scrollY - SHOW_START) / (SHOW_END - SHOW_START)
        )
      );

      // 2. DOM Mutation Guard (Zero updates when fully visible/hidden)
      if (header && progress !== lastProgressRef.current) {
        lastProgressRef.current = progress;

        // Pass progress directly to the CSS variable
        header.style.setProperty("--scroll-progress", progress);

        // Toggle active class safely
        if (progress > 0) {
          header.classList.add("active-header");
        } else {
          header.classList.remove("active-header");
        }
      }

      // 3. Throttled Section Spy (runs every 100ms for mobile CPU safety)
      let nextSection = activeSectionRef.current;
      let shouldCheckSections = false;

      if (now - lastDetectionTimeRef.current > 100) {
        shouldCheckSections = true;
        lastDetectionTimeRef.current = now;

        const detectionOffset = cachedToolbarHeightRef.current + STATIC_OFFSET + 100;
        nextSection = availableSections[0]?.id ?? "";

        for (const { id } of availableSections) {
          const element = document.getElementById(id);
          if (!element) continue;

          const top = element.getBoundingClientRect().top;
          if (top <= detectionOffset) {
            nextSection = id;
          } else {
            break;
          }
        }
      }

      if (
        shouldCheckSections &&
        nextSection &&
        nextSection !== activeSectionRef.current
      ) {
        activeSectionRef.current = nextSection;
        setActiveSection(nextSection);
      }
    };

    const handleScroll = () => {
      if (animationFrameRef.current !== null) return;
      animationFrameRef.current = window.requestAnimationFrame(updateFromScroll);
    };

    updateFromScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [availableSections, getHeaderOffset]);

  const { t } = useTranslation();

  if (!data) return null;

  const rankStr = data.rank ? `P${data.rank}` : null;
  const active = data?.activity?.padel?.length > 0;

  return (
    <HeaderBox ref={headerRef} component="header">
      <Box
        id="player-navigation-toolbar"
        sx={theme.mixins.toolbar}
      />

      <Stack
        direction="row"
        alignItems="center"
        sx={{
          pl: 1,
          pr: 1,
          pb: 1,
          borderBottom: "1px solid rgba(255,255,255,0.2)",
        }}
      >
        <PlayerAvatar
          initials={getInitials(data.name)}
          active={active}
        />
         
        <Box sx={{ color: "white", flexGrow: 1, marginLeft: 1 }}>
          <Typography
            variant="body1"
            component="div"
            fontWeight="bold"
            color="white"
            sx={{ opacity: 0.95, textTransform: "capitalize" }}
          >
            {data.name}
          </Typography>

          <Typography
            variant="caption"
            component="div"
            color="white"
            sx={{ opacity: 0.85 }}
          >
            {rankStr && (
              <b style={{ marginRight: 4 }}>
                {rankStr.replace(" ", "\u00A0")} •
              </b>
            )}
            {data.club}
          </Typography>
        </Box>

        <FavoriteButton
          playerId={data.player_id}
          name={data.name}
          club={data.club}
          color="white"
          sx={{ justifySelf: "flex-end" }}
        />
      </Stack>

      <Tabs
        value={activeSection || false}
        onChange={(_, newValue) => scrollToSection(newValue)}
        variant="scrollable"
        scrollButtons={false}
        allowScrollButtonsMobile={false}
        textColor="inherit"
        indicatorColor="inherit"
        aria-label={t("speler.pageSections")}
        sx={{
          minHeight: 32,
          "& .MuiTabs-flexContainer": { minHeight: 32 },
          "& .MuiTab-root": {
            minHeight: 32,
            py: 1.5,
            color: "rgba(255,255,255,0.7)",
            textTransform: "none",
          },
          "& .Mui-selected": { color: "#fff" },
          "& .MuiTabs-indicator": { height: 3, backgroundColor: "#fff" },
        }}
      >
        {availableSections.map(({ id, label }) => (
          <Tab key={id} label={label} value={id} />
        ))}
      </Tabs>
    </HeaderBox>
  );
}